import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PanelId, PanelConfig } from '../../../core/types/panel.types';
import { usePanelStore } from '../../store/panelStore';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';
import { Panel } from '../panels/Panel';
import { cn } from '../../utils/cn';
import * as Icons from 'lucide-react';

// Import implemented panel components
import { HierarchyPanel } from '../hierarchy';
import { SceneViewPanel } from '../scene-view';
import { InspectorPanel } from '../inspector';
import { ProjectPanel } from '../project';
import { ScriptEditorPanel } from '../script-editor';
import { AnimationEditorPanel } from '../animation-editor';
import { LivePreviewPanel } from '../live-preview';
import { ConsolePanel } from '../console';
import { SlotAssetGeneratorPanel } from '../slot-asset-generator';
import { SlotScreenGeneratorPanel } from '../slot-screen-generator';
import { AssetPreviewPanel } from '../asset-preview';
import { SlotCoreInstallToast } from '../slot-core/SlotCoreInstallToast';

// Panel content map - null means the panel handles its own wrapper
const PANEL_CONTENT: Record<PanelId, React.FC | null> = {
  'hierarchy': null,
  'scene-view': null,
  'inspector': null,
  'project': null,
  'script-editor': null,
  'animation-editor': null,
  'live-preview': null,
  'console': null,
  'slot-asset-generator': null,
  'slot-screen-generator': null,
};

// Splitter component for resizing panels
interface PanelSplitterProps {
  direction: 'horizontal' | 'vertical';
  onResize: (delta: number) => void;
}

const PanelSplitter: React.FC<PanelSplitterProps> = ({ direction, onResize }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    let lastPos = direction === 'horizontal' ? e.clientX : e.clientY;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const currentPos = direction === 'horizontal' ? moveEvent.clientX : moveEvent.clientY;
      const delta = currentPos - lastPos;
      lastPos = currentPos;
      onResize(delta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      className={cn(
        'flex-shrink-0 bg-zinc-800 hover:bg-blue-500 transition-colors',
        direction === 'horizontal' ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize',
        isDragging && 'bg-blue-500'
      )}
      onMouseDown={handleMouseDown}
    />
  );
};

// Minimized Panel Taskbar Item
interface MinimizedPanelItemProps {
  panel: PanelConfig;
  onRestore: () => void;
}

const MinimizedPanelItem: React.FC<MinimizedPanelItemProps> = ({ panel, onRestore }) => {
  const IconComponent = (Icons as Record<string, React.FC<{ size?: number; className?: string }>>)[panel.icon] || Icons.Box;

  return (
    <button
      className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-all duration-200 border border-zinc-700 animate-panel-open hover:scale-105"
      onClick={onRestore}
      title={`Restore ${panel.title}`}
    >
      <IconComponent size={14} className="text-zinc-400" />
      <span className="text-sm text-zinc-300">{panel.title}</span>
    </button>
  );
};

// Minimized Asset Preview Taskbar Item
interface MinimizedAssetPreviewItemProps {
  assetName: string;
  assetType: string;
  isDirty: boolean;
  onRestore: () => void;
}

const MinimizedAssetPreviewItem: React.FC<MinimizedAssetPreviewItemProps> = ({
  assetName,
  assetType,
  isDirty,
  onRestore,
}) => {
  const getAssetIcon = (type: string): React.FC<{ size?: number; className?: string }> => {
    switch (type) {
      case 'sprite':
        return Icons.Image;
      case 'audio-music':
      case 'audio-sfx':
        return Icons.Music;
      case 'prefab':
        return Icons.Package;
      default:
        return Icons.File;
    }
  };

  const IconComponent = getAssetIcon(assetType);

  return (
    <button
      className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-all duration-200 border border-zinc-700 hover:scale-105"
      onClick={onRestore}
      title={`Restore ${assetName}`}
    >
      <IconComponent size={14} className="text-zinc-400" />
      <span className="text-sm text-zinc-300 truncate max-w-[100px]">
        {assetName}
        {isDirty && <span className="text-yellow-500 ml-0.5">*</span>}
      </span>
    </button>
  );
};

/** Render the correct component for a given panel ID */
function renderPanel(panelId: PanelId): React.ReactNode {
  switch (panelId) {
    case 'scene-view': return <SceneViewPanel />;
    case 'hierarchy': return <HierarchyPanel />;
    case 'inspector': return <InspectorPanel />;
    case 'project': return <ProjectPanel />;
    case 'script-editor': return <ScriptEditorPanel />;
    case 'animation-editor': return <AnimationEditorPanel />;
    case 'live-preview': return <LivePreviewPanel />;
    case 'console': return <ConsolePanel />;
    case 'slot-asset-generator': return <SlotAssetGeneratorPanel />;
    case 'slot-screen-generator': return <SlotScreenGeneratorPanel />;
    default: return null;
  }
}

export const EditorLayout: React.FC = () => {
  const { panels, getPanelsByPosition, restorePanel, clampFloatingPanelsToViewport } = usePanelStore();
  const { getVisiblePreviews, getMinimizedPreviews, restorePreview } = useAssetPreviewStore();

  // Split sizes
  const [leftWidth, setLeftWidth] = useState(panels['hierarchy']?.defaultWidth || 250);
  const [rightWidth, setRightWidth] = useState(panels['inspector']?.defaultWidth || 300);
  const [bottomHeight, setBottomHeight] = useState(220);

  // Window resize handler - clamp panels to viewport bounds
  const resizeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = window.setTimeout(() => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const minTop = 32;

        setLeftWidth((prev) => Math.min(prev, Math.max(150, viewportWidth * 0.4)));
        setRightWidth((prev) => Math.min(prev, Math.max(200, viewportWidth * 0.4)));
        setBottomHeight((prev) => Math.min(prev, Math.max(100, (viewportHeight - minTop) * 0.5)));

        clampFloatingPanelsToViewport();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [clampFloatingPanelsToViewport]);

  const leftPanels = getPanelsByPosition('left');
  const centerPanels = getPanelsByPosition('center');
  const rightPanels = getPanelsByPosition('right');
  const bottomPanels = getPanelsByPosition('bottom');

  const floatingPanels = Object.values(panels).filter(
    (p) => p.visible && p.state === 'floating'
  );

  const minimizedPanels = Object.values(panels).filter(
    (p) => p.visible && p.state === 'minimized'
  );

  const maximizedPanel = Object.values(panels).find(
    (p) => p.visible && p.state === 'maximized'
  );

  const visibleAssetPreviews = getVisiblePreviews();
  const minimizedAssetPreviews = getMinimizedPreviews();

  const handleLeftResize = useCallback((delta: number) => {
    setLeftWidth((prev) => Math.max(150, Math.min(500, prev + delta)));
  }, []);

  const handleRightResize = useCallback((delta: number) => {
    setRightWidth((prev) => Math.max(200, Math.min(500, prev - delta)));
  }, []);

  const handleBottomResize = useCallback((delta: number) => {
    setBottomHeight((prev) => Math.max(100, Math.min(window.innerHeight * 0.5, prev - delta)));
  }, []);

  useEffect(() => {
    const handleConsoleResize = (e: CustomEvent<{ delta: number }>) => {
      handleBottomResize(-e.detail.delta);
    };

    window.addEventListener('console:resize' as never, handleConsoleResize as EventListener);
    return () => {
      window.removeEventListener('console:resize' as never, handleConsoleResize as EventListener);
    };
  }, [handleBottomResize]);

  const renderPanelContent = (panelId: PanelId) => {
    const Content = PANEL_CONTENT[panelId];
    return Content ? <Content /> : null;
  };

  // If a panel is maximized, render only that panel
  if (maximizedPanel) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        {renderPanel(maximizedPanel.id) || (
          <Panel panel={maximizedPanel}>
            {renderPanelContent(maximizedPanel.id)}
          </Panel>
        )}

        {/* Still render floating panels on top */}
        {floatingPanels.map((panel) => (
          renderPanel(panel.id) || (
            <Panel key={panel.id} panel={panel}>
              {renderPanelContent(panel.id)}
            </Panel>
          )
        ))}

        {/* Minimized panels taskbar */}
        {(minimizedPanels.length > 0 || minimizedAssetPreviews.length > 0) && (
          <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 px-4 py-2 bg-zinc-900 border-t border-zinc-700">
            <span className="text-xs text-zinc-500 mr-2">Minimized:</span>
            {minimizedPanels.map((panel) => (
              <MinimizedPanelItem
                key={panel.id}
                panel={panel}
                onRestore={() => restorePanel(panel.id)}
              />
            ))}
            {minimizedAssetPreviews.map((preview) => (
              <MinimizedAssetPreviewItem
                key={preview.id}
                assetName={preview.assetName}
                assetType={preview.assetType}
                isDirty={preview.isDirty}
                onRestore={() => restorePreview(preview.id)}
              />
            ))}
          </div>
        )}

        {/* Asset Preview Panels */}
        {visibleAssetPreviews.map((preview) => (
          <AssetPreviewPanel key={preview.id} instance={preview} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panels */}
        {leftPanels.length > 0 && (
          <>
            <div style={{ width: leftWidth }} className="flex flex-col overflow-hidden">
              {leftPanels.map((panel) => (
                <React.Fragment key={panel.id}>
                  {renderPanel(panel.id) || (
                    <Panel panel={panel} className="flex-1">
                      {renderPanelContent(panel.id)}
                    </Panel>
                  )}
                </React.Fragment>
              ))}
            </div>
            <PanelSplitter direction="horizontal" onResize={handleLeftResize} />
          </>
        )}

        {/* Center panels */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {centerPanels.length > 0 && (
            <div className="flex-1 flex overflow-hidden">
              {centerPanels.map((panel, index) => (
                <React.Fragment key={panel.id}>
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {renderPanel(panel.id) || (
                      <Panel panel={panel} className="flex-1">
                        {renderPanelContent(panel.id)}
                      </Panel>
                    )}
                  </div>
                  {index < centerPanels.length - 1 && (
                    <PanelSplitter direction="horizontal" onResize={() => {}} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Right panels */}
        {rightPanels.length > 0 && (
          <>
            <PanelSplitter direction="horizontal" onResize={handleRightResize} />
            <div style={{ width: rightWidth }} className="flex flex-col overflow-hidden">
              {rightPanels.map((panel, index) => (
                <React.Fragment key={panel.id}>
                  <div className="flex-1 min-h-0 overflow-hidden">
                    {renderPanel(panel.id) || (
                      <Panel panel={panel} className="flex-1 min-h-0">
                        {renderPanelContent(panel.id)}
                      </Panel>
                    )}
                  </div>
                  {index < rightPanels.length - 1 && (
                    <PanelSplitter direction="vertical" onResize={() => {}} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Bottom panels */}
      {bottomPanels.length > 0 && (
        <>
          <PanelSplitter direction="vertical" onResize={handleBottomResize} />
          <div style={{ height: bottomHeight }} className="flex overflow-hidden">
            {bottomPanels.map((panel, index) => (
              <React.Fragment key={panel.id}>
                <div className="flex-1 overflow-hidden">
                  {renderPanel(panel.id) || (
                    <Panel panel={panel} className="flex-1">
                      {renderPanelContent(panel.id)}
                    </Panel>
                  )}
                </div>
                {index < bottomPanels.length - 1 && (
                  <PanelSplitter direction="horizontal" onResize={() => {}} />
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {/* Minimized panels taskbar */}
      {(minimizedPanels.length > 0 || minimizedAssetPreviews.length > 0) && (
        <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border-t border-zinc-700">
          <span className="text-xs text-zinc-500 mr-2">Minimized:</span>
          {minimizedPanels.map((panel) => (
            <MinimizedPanelItem
              key={panel.id}
              panel={panel}
              onRestore={() => restorePanel(panel.id)}
            />
          ))}
          {minimizedAssetPreviews.map((preview) => (
            <MinimizedAssetPreviewItem
              key={preview.id}
              assetName={preview.assetName}
              assetType={preview.assetType}
              isDirty={preview.isDirty}
              onRestore={() => restorePreview(preview.id)}
            />
          ))}
        </div>
      )}

      {/* Floating panels */}
      {floatingPanels.map((panel) => (
        <React.Fragment key={panel.id}>
          {renderPanel(panel.id) || (
            <Panel key={panel.id} panel={panel}>
              {renderPanelContent(panel.id)}
            </Panel>
          )}
        </React.Fragment>
      ))}

      {/* Asset Preview Panels */}
      {visibleAssetPreviews.map((preview) => (
        <AssetPreviewPanel key={preview.id} instance={preview} />
      ))}

      {/* Slot Core Install/Update Toast */}
      <SlotCoreInstallToast />
    </div>
  );
};
